<template>
  <v-card
    v-if="draftsWithOverrides.length > 0"
    class="border mb-4"
    elevation="0"
    data-testid="publish-preview-pending-manual-overrides"
  >
    <v-card-title class="mb-2 bg-blue-grey-lighten-5 d-flex align-center">
      <v-icon icon="mdi-pencil-lock" class="mr-2" />
      Pending manual overrides
    </v-card-title>
    <v-card-text>
      <v-alert type="info" variant="tonal" density="compact" class="mb-4">
        These feed-backed field overrides are staged on vehicle drafts and will be committed when you
        publish.
      </v-alert>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3" />

      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        density="compact"
        class="mb-3"
      >
        {{ error }}
      </v-alert>

      <v-expansion-panels v-if="!loading" class="border" elevation="0" multiple>
        <v-expansion-panel v-for="draft in draftsWithOverrides" :key="draft.vin">
          <v-expansion-panel-title @click="loadDraftOverrides(draft.vin)">
            <div class="d-flex align-center justify-space-between w-100">
              <strong>{{ draft.vin }}</strong>
              <v-chip size="x-small" variant="tonal" color="primary">
                {{ draft.manualOverrideActionKeys?.length ?? 0 }} override(s)
              </v-chip>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <VehicleDraftManualOverridesTable
              v-if="overrideRowsByVin[draft.vin]?.length"
              :vin="draft.vin"
              :rows="overrideRowsByVin[draft.vin]"
              :dealer-time-zone="dealerTimeZone"
              photo-mode="new-only"
            />
            <div v-else-if="overrideLoadingByVin[draft.vin]" class="text-body-2 text-grey">
              Loading override details…
            </div>
            <div v-else class="text-body-2 text-grey">No override details loaded yet.</div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import VehicleDraftManualOverridesTable from '@/components/VehicleDraftManualOverridesTable.vue'
import {
  buildManualOverrideDraftDisplayRows,
  type ManualOverrideDraftDisplayRow,
} from '@/utils/manualOverrideDraftDisplay'

type DraftSummary = {
  vin: string
  manualOverrideActionKeys?: string[]
}

const accountSettings = useAccountSettingsStore()
const dealerTimeZone = computed(() => String(accountSettings.dealerTimeZone || '').trim())
const iseBackendUrl = import.meta.env.VITE_ISE_BACKEND_URL

const loading = ref(false)
const error = ref<string | null>(null)
const draftsWithOverrides = ref<DraftSummary[]>([])
const overrideRowsByVin = ref<Record<string, ManualOverrideDraftDisplayRow[]>>({})
const overrideLoadingByVin = ref<Record<string, boolean>>({})
const overrideLoadedByVin = ref<Record<string, boolean>>({})

const normalizeVin = (vinRaw: string): string =>
  String(vinRaw || '')
    .trim()
    .toUpperCase()

const fetchDraftSummaries = async () => {
  const jazelId = accountSettings.accountId
  if (!iseBackendUrl || !jazelId) return

  loading.value = true
  error.value = null
  try {
    const base = String(iseBackendUrl).replace(/\/+$/g, '')
    const url = new URL(`${base}/vehicle-drafts`)
    url.searchParams.set('jazelId', jazelId)

    const headers: Record<string, string> = {}
    if (accountSettings.sessionToken) {
      headers.authorization = `Bearer ${accountSettings.sessionToken}`
    }

    const resp = await fetch(url.toString(), { headers })
    if (!resp.ok) throw new Error(`Failed to load vehicle drafts (${resp.status})`)

    const json: any = await resp.json()
    const drafts = Array.isArray(json?.drafts) ? json.drafts : []
    draftsWithOverrides.value = drafts
      .map((d: any) => ({
        vin: normalizeVin(d?.vin),
        manualOverrideActionKeys: Array.isArray(d?.manualOverrideActionKeys)
          ? d.manualOverrideActionKeys
          : [],
      }))
      .filter(
        (d: DraftSummary) =>
          Boolean(d.vin) && Array.isArray(d.manualOverrideActionKeys) && d.manualOverrideActionKeys.length > 0,
      )
  } catch (e: any) {
    error.value = e?.message || 'Failed to load pending manual overrides'
    draftsWithOverrides.value = []
  } finally {
    loading.value = false
  }
}

const loadDraftOverrides = async (vinRaw: string) => {
  const vin = normalizeVin(vinRaw)
  if (!vin || overrideLoadedByVin.value[vin]) return

  const jazelId = accountSettings.accountId
  if (!iseBackendUrl || !jazelId) return

  overrideLoadingByVin.value = { ...overrideLoadingByVin.value, [vin]: true }
  try {
    const base = String(iseBackendUrl).replace(/\/+$/g, '')
    const url = new URL(`${base}/vehicle-drafts/history`)
    url.searchParams.set('jazelId', jazelId)
    url.searchParams.set('vin', vin)
    url.searchParams.set('limit', '200')

    const headers: Record<string, string> = {}
    if (accountSettings.sessionToken) {
      headers.authorization = `Bearer ${accountSettings.sessionToken}`
    }

    const resp = await fetch(url.toString(), { headers })
    if (!resp.ok) throw new Error(`Failed to load override history (${resp.status})`)

    const json: any = await resp.json()
    overrideRowsByVin.value = {
      ...overrideRowsByVin.value,
      [vin]: buildManualOverrideDraftDisplayRows(json?.manualOverrideActions),
    }
    overrideLoadedByVin.value = { ...overrideLoadedByVin.value, [vin]: true }
  } catch (e: any) {
    console.warn(`Failed to load manual override details for ${vin}`, e)
    overrideRowsByVin.value = { ...overrideRowsByVin.value, [vin]: [] }
    overrideLoadedByVin.value = { ...overrideLoadedByVin.value, [vin]: true }
  } finally {
    overrideLoadingByVin.value = { ...overrideLoadingByVin.value, [vin]: false }
  }
}

onMounted(() => {
  fetchDraftSummaries().catch(() => {})
})
</script>
